import { ReactNode, useEffect, useState } from "react";
import { useIsomorphicLayoutEffect } from "react-use";
import styled from "@emotion/styled";
import {
  createImpactShopBasketProduct,
  ImpactShopProductLink,
  ImpactShopProductType,
  locationCurrencyCodeMapper,
  PlatformFeature,
} from "@ecologi/shared";
import { ImpactShopProductPageImage } from "@ecologi/react-components/src/ImpactShopProductPage/ImpactShopProductPageImage";
import { ImpactShopProductPageSidebar } from "@ecologi/react-components/src/ImpactShopProductPage/ImpactShopProductPageSidebar";
import { ImpactShopProductPagePanel } from "@ecologi/react-components/src/ImpactShopProductPage/ImpactShopProductPagePanel";
import { ImpactShopProductPageCards } from "@ecologi/react-components/src/ImpactShopProductPage/ImpactShopProductPageCards";
import { ImpactShopProductPageBenefits } from "@ecologi/react-components/src/ImpactShopProductPage/ImpactShopProductPageBenefits";
import { Head } from "../../../components";
import { useFetchImpactShopStore } from "../../../store/impact-shop";
import { useStore } from "../../../store";
import { pushImpactShopProductAddToBasket } from "../../../lib";
import { useGetConfigCatValue } from "../../../lib/configcat";
import { useListImpactGroups } from "../hooks/useListImpactGroups";
import { useCreateImpactGroup } from "../hooks/useCreateImpactGroup";
import BackIcon from "@ecologi/assets/icons/back-icon.svg";
import {
  BasketLink,
  IconLink,
  TopNavigation,
} from "@ecologi/react-components/src";
import { useImpactShopMessage } from "../hooks";

const useImpactShopStore = useFetchImpactShopStore();

const Page = styled.div`
  display: grid;

  @media ${({ theme }) => theme.bp.desktopUp} {
    grid-template-columns: minmax(0, 1fr) 339px;
  }
  @media ${({ theme }) => theme.bp.wideDesktopUp} {
    grid-template-columns: minmax(0, 1fr) 383px;
  }
  @media ${({ theme }) => theme.bp.widerDesktopUp} {
    grid-template-columns: minmax(0, 1fr) 408px;
  }
  @media ${({ theme }) => theme.bp.extraWideDesktopUp} {
    grid-template-columns: minmax(0, 1fr) 430px;
  }
`;

const ProjectInfoContainer = styled(ImpactShopProductPagePanel)`
  background-color: ${({ theme }) => theme.colors.mistLight};
  padding-bottom: 20px;
  width: 100%;

  @media ${(props) => props.theme.bp.desktopUp} {
    background-color: ${({ theme }) => theme.colors.snowdrop};
  }
`;

const WhyMoreInfoContainer = styled(ImpactShopProductPagePanel)`
  background-color: ${({ theme }) => theme.colors.snowdrop};
  width: 100%;

  @media ${({ theme }) => theme.bp.desktopUp} {
    background-color: ${({ theme }) => theme.colors.mistLight};
  }
`;

const FundBreakdownList = styled.ul`
  line-height: 1.5;
  padding-left: 15px;
  margin-bottom: 24px;

  li {
    @counter-style tilde-list {
      system: cyclic;
      symbols: "~";
      suffix: " ";
    }

    list-style: tilde-list;
  }
`;

const StyledLink = styled.a`
  display: inline;
  color: ${({ theme }) => theme.colors.charcoal};
  text-decoration: underline;
`;

export type ImpactShopProductPageProps = {
  isLoggedIn: boolean;
  product: ImpactShopProductType;
  impactGroupsEnabled?: boolean;
};

export const ImpactShopProductPage = ({
  isLoggedIn,
  product,
  impactGroupsEnabled: impactGroupsEnabledFromSSR,
}: ImpactShopProductPageProps) => {
  const { location } = useStore();
  const { setCurrency } = useImpactShopStore();
  const [basketQty, setBasketQty] = useState(0);

  const [impactGroupsEnabledFromConfig] = useGetConfigCatValue(
    PlatformFeature.ImpactGroupsEnabled,
    false
  );

  const impactGroupsEnabled =
    impactGroupsEnabledFromSSR ?? impactGroupsEnabledFromConfig;

  useEffect(() => {
    setCurrency(locationCurrencyCodeMapper[location.countryCode]);
  }, [location]);

  const { addProductToBasket, currency, totalNumberOfProductsInBasket } =
    useImpactShopStore();

  useIsomorphicLayoutEffect(() => {
    setBasketQty(totalNumberOfProductsInBasket());
  }, [totalNumberOfProductsInBasket]);

  const { data: groups = [] } = useListImpactGroups(
    impactGroupsEnabled && isLoggedIn
  );
  const createGroup = useCreateImpactGroup();

  const handleAddToBasket = (
    product: ImpactShopProductType,
    quantity: number,
    fundedBy: string,
    groupId?: number
  ) => {
    if (quantity) {
      addProductToBasket(
        createImpactShopBasketProduct(product, quantity, fundedBy, groupId)
      );
      pushImpactShopProductAddToBasket({
        slug: product.slug,
        quantity,
      });
    }
  };

  const renderLink = (
    links: ImpactShopProductLink[],
    index: number
  ): ReactNode | null => {
    return links[index] ? (
      <StyledLink href={links[index].url} target="_blank" rel="noopener">
        {links[index].text}
      </StyledLink>
    ) : null;
  };

  useImpactShopMessage({
    redirectTo: "/impact-shop/basket",
  });

  return (
    <>
      <Head title={`${product.display.title} | Impact shop`} />
      <TopNavigation
        loggedOutNav={!isLoggedIn}
        leftSlot={
          <IconLink label="Shop" href="/impact-shop" startSlot={<BackIcon />} />
        }
        rightSlot={
          <BasketLink href="/impact-shop/basket" itemQuantity={basketQty} />
        }
      />
      <Page>
        <ImpactShopProductPageImage
          title={product.display.title}
          image={product.display.images[0]}
          badgePath={product.display.badgePath}
        />
        <ImpactShopProductPageSidebar
          currency={currency}
          groups={groups}
          impactGroupsEnabled={impactGroupsEnabled}
          isLoggedIn={isLoggedIn}
          onAddToBasket={handleAddToBasket}
          onCreateGroup={createGroup}
          product={product}
        />
        <ProjectInfoContainer
          fundBreakdown={
            product.display.fundBreakdown ? (
              <FundBreakdownList data-testid="fund-breakdown">
                {product.display.fundBreakdown.map((project) => (
                  <li key={project.text}>
                    {`${project.percentage}% `}
                    <StyledLink
                      href={project.path}
                      target="_blank"
                      rel="noopener"
                    >
                      {project.text}
                    </StyledLink>
                  </li>
                ))}
              </FundBreakdownList>
            ) : null
          }
          header="The impact you will be making"
          summaryText={product.display.summary}
          link={renderLink(product.display.links, 0)}
        >
          <ImpactShopProductPageCards projectInfo={product.display} />
        </ProjectInfoContainer>
        <WhyMoreInfoContainer
          fundBreakdown={null}
          header={product.display.whyMoreTitle}
          link={renderLink(product.display.links, 1)}
          summaryText={product.display.whyMoreBody}
        >
          <ImpactShopProductPageBenefits benefits={product.display.benefits} />
        </WhyMoreInfoContainer>
      </Page>
    </>
  );
};
